import type { JSX, ReactNode } from 'react';

interface AdfMark {
  type: string;
  attrs?: Record<string, unknown>;
}

interface AdfNode {
  type: string;
  text?: string;
  attrs?: Record<string, unknown>;
  marks?: AdfMark[];
  content?: AdfNode[];
}

const ALLOWED_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);

function safeHref(url: unknown): string | null {
  if (typeof url !== 'string') return null;
  try {
    const parsed = new URL(url);
    return ALLOWED_PROTOCOLS.has(parsed.protocol) ? parsed.toString() : null;
  } catch {
    return null;
  }
}

function applyMarks(text: string, marks: AdfMark[] | undefined, key: string): ReactNode {
  if (!marks || marks.length === 0) return text;

  let node: ReactNode = text;
  for (const mark of marks) {
    switch (mark.type) {
      case 'strong':
        node = <strong key={`${key}-b`}>{node}</strong>;
        break;
      case 'em':
        node = <em key={`${key}-i`}>{node}</em>;
        break;
      case 'underline':
        node = <u key={`${key}-u`}>{node}</u>;
        break;
      case 'strike':
        node = <s key={`${key}-s`}>{node}</s>;
        break;
      case 'code':
        node = <code key={`${key}-c`}>{node}</code>;
        break;
      case 'link': {
        const href = safeHref(mark.attrs?.href);
        node = href ? (
          <a key={`${key}-a`} href={href} target="_blank" rel="noopener noreferrer">
            {node}
          </a>
        ) : (
          node
        );
        break;
      }
      case 'subsup':
        node =
          mark.attrs?.type === 'sub' ? (
            <sub key={`${key}-sub`}>{node}</sub>
          ) : (
            <sup key={`${key}-sup`}>{node}</sup>
          );
        break;
      default:
        break;
    }
  }
  return node;
}

const PANEL_TONE: Record<string, string> = {
  info: 'rgba(36,129,204,0.12)',
  note: 'rgba(120,120,180,0.12)',
  warning: 'rgba(240,180,40,0.16)',
  success: 'rgba(60,180,110,0.14)',
  error: 'rgba(223,63,64,0.14)',
};

const MAX_DEPTH = 24;

function renderNodes(nodes: AdfNode[] | undefined, prefix: string, depth = 0): ReactNode[] {
  if (depth > MAX_DEPTH) return [];
  return (nodes ?? []).map((node, index) => renderNode(node, `${prefix}-${index}`, depth + 1));
}

function renderNode(node: AdfNode, key: string, depth = 0): ReactNode {
  if (depth > MAX_DEPTH) return null;
  switch (node.type) {
    case 'doc':
      return <div key={key}>{renderNodes(node.content, key, depth)}</div>;

    case 'paragraph':
      return <p key={key}>{renderNodes(node.content, key, depth)}</p>;

    case 'text':
      return <span key={key}>{applyMarks(node.text ?? '', node.marks, key)}</span>;

    case 'hardBreak':
      return <br key={key} />;

    case 'heading': {
      const level = Math.min(Math.max(Number(node.attrs?.level ?? 1), 1), 6);
      const Tag = `h${level}` as keyof JSX.IntrinsicElements;
      return <Tag key={key}>{renderNodes(node.content, key, depth)}</Tag>;
    }

    case 'bulletList':
      return <ul key={key}>{renderNodes(node.content, key, depth)}</ul>;

    case 'orderedList':
      return (
        <ol key={key} start={Number(node.attrs?.order ?? 1)}>
          {renderNodes(node.content, key, depth)}
        </ol>
      );

    case 'listItem':
      return <li key={key}>{renderNodes(node.content, key, depth)}</li>;

    case 'codeBlock':
      return (
        <pre key={key}>
          <code>{(node.content ?? []).map((child) => child.text ?? '').join('')}</code>
        </pre>
      );

    case 'blockquote':
      return <blockquote key={key}>{renderNodes(node.content, key, depth)}</blockquote>;

    case 'panel': {
      const tone = PANEL_TONE[String(node.attrs?.panelType ?? 'info')] ?? PANEL_TONE.info;
      return (
        <div key={key} className="panel" style={{ background: tone }}>
          {renderNodes(node.content, key, depth)}
        </div>
      );
    }

    case 'rule':
      return <hr key={key} />;

    case 'mention':
      return (
        <span key={key} className="mention">
          {String(node.attrs?.text ?? '@unknown')}
        </span>
      );

    case 'emoji':
      return <span key={key}>{String(node.attrs?.text ?? node.attrs?.shortName ?? '')}</span>;

    case 'date': {
      const ts = Number(node.attrs?.timestamp);
      return (
        <span key={key} className="chip">
          {Number.isFinite(ts) ? new Date(ts).toISOString().slice(0, 10) : ''}
        </span>
      );
    }

    case 'status':
      return (
        <span key={key} className="chip">
          {String(node.attrs?.text ?? '')}
        </span>
      );

    case 'table':
      return (
        <table key={key}>
          <tbody>{renderNodes(node.content, key, depth)}</tbody>
        </table>
      );

    case 'tableRow':
      return <tr key={key}>{renderNodes(node.content, key, depth)}</tr>;

    case 'tableHeader':
      return (
        <th key={key} colSpan={Number(node.attrs?.colspan ?? 1)}>
          {renderNodes(node.content, key, depth)}
        </th>
      );

    case 'tableCell':
      return (
        <td key={key} colSpan={Number(node.attrs?.colspan ?? 1)}>
          {renderNodes(node.content, key, depth)}
        </td>
      );

    case 'mediaSingle':
    case 'mediaGroup':
      return (
        <div key={key} className="chip">
          📎 {(node.content ?? []).length}
        </div>
      );

    case 'inlineCard':
    case 'blockCard':
    case 'embedCard': {
      const href = safeHref(node.attrs?.url);
      return href ? (
        <a key={key} href={href} target="_blank" rel="noopener noreferrer">
          {href}
        </a>
      ) : null;
    }

    case 'expand':
    case 'nestedExpand':
      return (
        <details key={key}>
          <summary>{String(node.attrs?.title ?? 'Details')}</summary>
          {renderNodes(node.content, key, depth)}
        </details>
      );

    case 'taskList':
      return <ul key={key}>{renderNodes(node.content, key, depth)}</ul>;

    case 'taskItem':
      return (
        <li key={key}>
          {node.attrs?.state === 'DONE' ? '☑ ' : '☐ '}
          {renderNodes(node.content, key, depth)}
        </li>
      );

    case 'decisionList':
      return <ul key={key}>{renderNodes(node.content, key, depth)}</ul>;

    case 'decisionItem':
      return <li key={key}>✔ {renderNodes(node.content, key, depth)}</li>;

    default:
      // Unknown node types render their children rather than dropping content.
      return node.content ? <span key={key}>{renderNodes(node.content, key, depth)}</span> : null;
  }
}

export function Adf({ doc }: { doc: unknown }): JSX.Element | null {
  // Data Center returns wiki markup as a plain string. Collapsing its newlines
  // turns a structured description into one unreadable paragraph.
  if (typeof doc === 'string') {
    return doc.trim().length === 0 ? null : (
      <p className="adf" style={{ whiteSpace: 'pre-wrap' }}>
        {doc}
      </p>
    );
  }
  if (!doc || typeof doc !== 'object') return null;
  const root = doc as AdfNode;
  if (root.type !== 'doc') return null;
  return <div className="adf">{renderNodes(root.content, 'adf')}</div>;
}
